import React, { createContext, useContext, useEffect, useRef, useState, useCallback } from "react";
import { getSunbedSessions, finishSunbedSession, SunbedSessionApiItem } from "../services/sunbeds";
import { cancelTmaxSession } from "../services/tmax";
import { useAuth } from "../auth/AuthContext";

interface SunbedSessionTimer {
  sessionId: number | string;
  delayLeft: number;
  remainingLeft: number;
  phase: "delay" | "active" | "completed";
  startAtMs: number | null;
  endsAtMs: number | null;
  skewMs: number;
  tmaxId?: number | string;
}

interface SunbedSessionsContextType {
  activeSessions: SunbedSessionApiItem[];
  refreshSessions: () => Promise<void>;
  isSessionActive: (sessionId: number | string) => boolean;
}

const SunbedSessionsContext = createContext<SunbedSessionsContextType | undefined>(undefined);

function parseMs(value?: string | null): number | null {
  if (!value) return null;
  const ms = Date.parse(value);
  return Number.isFinite(ms) ? ms : null;
}

function deriveFromSession(session: SunbedSessionApiItem): Omit<SunbedSessionTimer, "sessionId" | "tmaxId"> {
  const startAtMs = parseMs(session.start_at);
  const endsAtMs = parseMs(session.ends_at);
  const serverMs = parseMs(session.server_now);
  const skewMs = serverMs != null ? serverMs - Date.now() : 0;
  const now = Date.now() + skewMs;

  if (startAtMs != null && endsAtMs != null) {
    if (now < startAtMs) {
      return {
        delayLeft: Math.max(0, Math.ceil((startAtMs - now) / 1000)),
        remainingLeft: Math.max(0, Math.ceil((endsAtMs - startAtMs) / 1000)),
        phase: "delay",
        startAtMs,
        endsAtMs,
        skewMs,
      };
    }
    const left = Math.max(0, Math.ceil((endsAtMs - now) / 1000));
    return {
      delayLeft: 0,
      remainingLeft: left,
      phase: left > 0 ? "active" : "completed",
      startAtMs,
      endsAtMs,
      skewMs,
    };
  }

  const delay = session.mins_left?.delay || 0;
  const remaining = session.mins_left?.remaining || 0;
  if (delay > 0) {
    return {
      delayLeft: delay,
      remainingLeft: remaining,
      phase: "delay",
      startAtMs: null,
      endsAtMs: null,
      skewMs: 0,
    };
  }
  return {
    delayLeft: 0,
    remainingLeft: remaining,
    phase: remaining > 0 ? "active" : "completed",
    startAtMs: null,
    endsAtMs: null,
    skewMs: 0,
  };
}

export const SunbedSessionsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user } = useAuth();
  const [activeSessions, setActiveSessions] = useState<SunbedSessionApiItem[]>([]);
  const timersRef = useRef<Map<number | string, SunbedSessionTimer>>(new Map());
  const intervalsRef = useRef<Map<number | string, ReturnType<typeof setInterval>>>(new Map());
  const finishingRef = useRef<Set<string>>(new Set());

  const stopTimer = useCallback((sessionId: number | string) => {
    const intervalId = intervalsRef.current.get(sessionId);
    if (intervalId) {
      clearInterval(intervalId);
      intervalsRef.current.delete(sessionId);
    }
    timersRef.current.delete(sessionId);
  }, []);

  const handleSessionComplete = useCallback(
    async (sessionId: number | string, timer: SunbedSessionTimer) => {
      const key = String(sessionId);
      if (finishingRef.current.has(key)) return;
      finishingRef.current.add(key);

      try {
        stopTimer(sessionId);
        await finishSunbedSession(sessionId);

        const isTmaxEnabled = user?.company?.is_sunbed_tmax;
        if (isTmaxEnabled && timer.tmaxId) {
          try {
            await cancelTmaxSession(timer.tmaxId);
          } catch (tmaxError) {
            console.error("Failed to cancel TMAX session after finish", tmaxError);
          }
        }
      } catch (error) {
        console.error("Failed to finish session:", error);
      } finally {
        finishingRef.current.delete(key);
      }
    },
    [stopTimer, user]
  );

  const startTimer = useCallback(
    (sessionId: number | string, timer: SunbedSessionTimer) => {
      const existingInterval = intervalsRef.current.get(sessionId);
      if (existingInterval) {
        clearInterval(existingInterval);
      }

      const intervalId = setInterval(() => {
        const current = timersRef.current.get(sessionId);
        if (!current) {
          clearInterval(intervalId);
          intervalsRef.current.delete(sessionId);
          return;
        }

        const now = Date.now() + current.skewMs;
        let updated = { ...current };

        if (current.startAtMs != null && current.endsAtMs != null) {
          if (now < current.startAtMs) {
            updated.phase = "delay";
            updated.delayLeft = Math.max(0, Math.ceil((current.startAtMs - now) / 1000));
            updated.remainingLeft = Math.max(
              0,
              Math.ceil((current.endsAtMs - current.startAtMs) / 1000)
            );
          } else {
            const left = Math.max(0, Math.ceil((current.endsAtMs - now) / 1000));
            updated.phase = left > 0 ? "active" : "completed";
            updated.delayLeft = 0;
            updated.remainingLeft = left;
          }
        } else if (updated.phase === "delay") {
          if (updated.delayLeft > 0) {
            updated.delayLeft -= 1;
          } else {
            updated.phase = "active";
          }
        } else if (updated.phase === "active") {
          if (updated.remainingLeft > 0) {
            updated.remainingLeft -= 1;
          } else {
            updated.phase = "completed";
          }
        }

        timersRef.current.set(sessionId, updated);

        if (updated.phase === "completed") {
          clearInterval(intervalId);
          intervalsRef.current.delete(sessionId);
          void handleSessionComplete(sessionId, updated);
        }
      }, 1000);

      intervalsRef.current.set(sessionId, intervalId);
    },
    [handleSessionComplete]
  );

  const fetchSessions = useCallback(async () => {
    try {
      const response = await getSunbedSessions();
      if (!response?.data) return;

      const sessions = response.data || [];
      setActiveSessions(sessions);

      sessions.forEach((session) => {
        const sessionId = session.id;
        const isActive =
          session.status.label.toLowerCase() === "running" ||
          session.status.label.toLowerCase() === "in-session";

        if (!isActive) {
          stopTimer(sessionId);
          return;
        }

        const derived = deriveFromSession(session);
        if (derived.phase === "completed") {
          const timer: SunbedSessionTimer = {
            sessionId,
            tmaxId: session.tmax_id,
            ...derived,
          };
          timersRef.current.set(sessionId, timer);
          void handleSessionComplete(sessionId, timer);
          return;
        }

        const timer: SunbedSessionTimer = {
          sessionId,
          tmaxId: session.tmax_id,
          ...derived,
        };
        timersRef.current.set(sessionId, timer);
        startTimer(sessionId, timer);
      });

      const activeSessionIds = new Set(sessions.map((s) => String(s.id)));
      timersRef.current.forEach((_, sessionId) => {
        if (!activeSessionIds.has(String(sessionId))) {
          stopTimer(sessionId);
        }
      });
    } catch (error) {
      console.error("Failed to fetch sunbed sessions:", error);
    }
  }, [handleSessionComplete, startTimer, stopTimer]);

  useEffect(() => {
    if (!localStorage.getItem("token")) return;

    void fetchSessions();

    return () => {
      intervalsRef.current.forEach((intervalId) => clearInterval(intervalId));
      intervalsRef.current.clear();
      timersRef.current.clear();
    };
  }, [fetchSessions]);

  // Poll whenever any session is live so web + kiosk stay aligned
  useEffect(() => {
    const hasActiveSessions = activeSessions.some(
      (s) =>
        s.status.label.toLowerCase() === "running" ||
        s.status.label.toLowerCase() === "in-session"
    );

    if (!hasActiveSessions) return;

    const pollInterval = setInterval(() => {
      void fetchSessions();
    }, 5000);

    return () => clearInterval(pollInterval);
  }, [activeSessions, fetchSessions]);

  const isSessionActive = useCallback(
    (sessionId: number | string) => {
      return activeSessions.some(
        (s) =>
          s.id === sessionId &&
          (s.status.label.toLowerCase() === "running" ||
            s.status.label.toLowerCase() === "in-session")
      );
    },
    [activeSessions]
  );

  return (
    <SunbedSessionsContext.Provider
      value={{
        activeSessions,
        refreshSessions: fetchSessions,
        isSessionActive,
      }}
    >
      {children}
    </SunbedSessionsContext.Provider>
  );
};

export const useSunbedSessions = (): SunbedSessionsContextType => {
  const context = useContext(SunbedSessionsContext);
  if (context === undefined) {
    throw new Error("useSunbedSessions must be used within a SunbedSessionsProvider");
  }
  return context;
};
